// 茶屋のたまき（AIと話す・遊び方の相談。Documentation/AI_SUPPORT.md）のチャットチケット。
// AIの返事1回で1枚。Google と連携したアカウントだけ、最初に freeLinked 回まで無料（補充なし。ゲストは無料なし）。
// 月パスを買うと monthlyTickets 枚（購入ごと・購入から validDays 日の期限。返金・チャージバックでは同じ枚数を減らす）。
// 運営への連絡・運営の返事にはチケットを使わない。
// チケットはショップの「チャットチケット10枚」（products.ts の chat10）で買う。購入ごとに validDays 日の期限（Profile.chatLots）。
// 期限は資金決済法の前払式支払手段の適用除外（発行の日から6か月以内に限り使用できるもの）に収めるため。期限が過ぎた分は消える
// （ほかの物に替えない：替えると価値が6か月を超えて残る）。使うときは期限の近い購入から。
import type { Profile } from './profile';

export const SUPPORT = {
  /** 茶屋の女将（猫又・AI）の名前 */
  name: 'たまき',
  /** Google 連携したアカウントの無料の回数（通算。補充しない）。1回およそ1.5円で済んだので5→10に（2026-11-04） */
  freeLinked: 10,
  /** 月パスを買ったときに付けるチャットチケット（購入ごと） */
  monthlyTickets: 30,
  /** 購入したチケットの有効期限（日）。6か月より短く */
  validDays: 180,
} as const;

const DAY_MS = 86_400_000;
type Lot = { n: number; until: number };

/** 期限内の購入（期限の近い順）。旧データの期限なしの枚数（tickets.chat）は、次に書き換えるときに期限つきへ移す */
function lots(p: Pick<Profile, 'chatLots' | 'tickets'>, now: number): Lot[] {
  const out = (p.chatLots ?? []).filter((l) => l.until > now && l.n > 0).sort((a, b) => a.until - b.until);
  const legacy = Math.max(0, Math.floor(p.tickets?.chat ?? 0));
  if (legacy) out.push({ n: legacy, until: now + SUPPORT.validDays * DAY_MS });
  return out;
}
function settle(p: Profile, now: number) {
  p.chatLots = lots(p, now);
  if (p.tickets?.chat) delete p.tickets.chat;
}

/** 無料の残り（linked＝Google 連携済み。ゲストは0） */
export const supportFreeLeft = (p: Pick<Profile, 'supportFree'>, linked: boolean) => (linked ? Math.max(0, SUPPORT.freeLinked - Math.max(0, p.supportFree ?? 0)) : 0);
/** 持っているチャットチケット（期限内） */
export const supportTickets = (p: Pick<Profile, 'chatLots' | 'tickets'>, now: number) => lots(p, now).reduce((a, l) => a + l.n, 0);
/** いちばん近い期限とその枚数（無ければ null） */
export function supportTicketsExpiry(p: Pick<Profile, 'chatLots' | 'tickets'>, now: number): { until: number; n: number } | null {
  const l = lots(p, now)[0];
  return l ? { until: l.until, n: l.n } : null;
}
export const canAskSupport = (p: Pick<Profile, 'supportFree' | 'tickets' | 'chatLots'>, linked: boolean, now: number) => supportFreeLeft(p, linked) > 0 || supportTickets(p, now) > 0;

/** 購入：count 枚を今から validDays 日の期限で足す */
export function grantSupportTickets(p: Profile, count: number, now: number) {
  settle(p, now);
  p.chatLots!.push({ n: count, until: now + SUPPORT.validDays * DAY_MS });
}

/** 返金・チャージバック：count 枚を減らす（期限の遠い＝新しい購入から。使った分は戻さない） */
export function revokeSupportTickets(p: Profile, count: number, now: number) {
  settle(p, now);
  let left = count;
  for (let i = p.chatLots!.length - 1; i >= 0 && left > 0; i--) {
    const take = Math.min(left, p.chatLots![i].n);
    p.chatLots![i].n -= take;
    left -= take;
  }
  p.chatLots = p.chatLots!.filter((l) => l.n > 0);
}

/** AIの返事1回ぶんを使う（無料の回数が先、次に期限の近い購入）。使えなければ null。freeOk＝false（その日の無料の分の費用が上限）なら無料を飛ばしてチケット */
export function useSupportTicket(p: Profile, linked: boolean, now: number, freeOk = true): 'free' | 'ticket' | null {
  if (freeOk && supportFreeLeft(p, linked) > 0) {
    p.supportFree = Math.max(0, p.supportFree ?? 0) + 1;
    return 'free';
  }
  settle(p, now);
  const l = p.chatLots![0];
  if (!l) return null;
  l.n -= 1;
  p.chatLots = p.chatLots!.filter((x) => x.n > 0);
  return 'ticket';
}
